import SettingsTabBase from "./SettingsTabBase.jsx";
import {GRANTS} from "../../../other/constants/PrivilegeTypes.jsx";
import {SETTING_TAB_WIDGET_KEYS, IMPORT_TYPES, IMPORT_HEADERS} from "../../../other/constants/OtherKeys.jsx";
import { EDITOR_KEYS } from "../../../other/constants/EditorKeys.jsx";

export default class SettingsImportExportTab extends SettingsTabBase {
  static get $name() {
    return "SettingsImportExportTab";
  }

  // Factoryable type name
  static get type() {
    return "settingsimportexporttab";
  }

  get swivuDeepLinkSlug() {
    return SETTING_TAB_WIDGET_KEYS.importExportTab;
  }

  /**
   * Helper method to create a reusable import/export button configuration
   * @param {string} icon - Font Awesome icon class (e.g., 'fa-screwdriver-wrench')
   * @param {string} title - Button title text
   * @param {string} description - Button description text
   * @param {string} onClick - Click handler reference (e.g., 'up.onImportResources')
   * @returns {object} Button configuration object
   */
  static createImportButton(icon, title, description, onClick) {
    return {
      type: "button",
      cls: "!h-auto !py-4 !px-6 !text-left !justify-start disabled:opacity-50 hover:shadow-lg transition-all duration-200 !border !border-gray-200 dark:!border-gray-700 !rounded-lg hover:!border-blue-400 dark:hover:!border-blue-500",
      html: `
        <div class="flex items-center justify-start gap-3">
          <div class="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
            <i class="fa-light ${icon} text-base text-blue-600 dark:text-blue-400"></i>
          </div>
          <div class="flex-1">
            <div class="text-base font-semibold mb-1">${title}</div>
            <div class="text-xs text-gray-500 dark:text-gray-400">${description}</div>
          </div>
        </div>
      `,
      onClick
    };
  }

  static get configurable() {
    return {
      title: "Import/Export",
      cls: "p-[2em]",
      layout: "vbox",
      items: {
        importSectionLabel: {
          type: "widget",
          html: `<div class="text-sm text-gray-600 dark:text-gray-400 mb-6">Import or export various types of data. For full Board Export, see General Board Settings.</div>`
        },
        buttonGrid: {
          type: "container",
          cls: "grid grid-cols-1 md:grid-cols-2 gap-6",
          items: {
            importResourcesButton: this.createImportButton(
              'fa-screwdriver-wrench',
              'Resources',
              'Import or export resource data',
              'up.onImportResources'
            ),
            importTasksButton: this.createImportButton(
              'fa-swap-arrows',
              'Tasks',
              'Import or export task data',
              'up.onImportTasks'
            ),
            importClientsButton: this.createImportButton(
              'fa-building-user',
              'Clients',
              'Import or export client data',
              'up.onImportClients'
            ),
            importBudgetButton: this.createImportButton(
              'fa-coins',
              'Budget',
              'Import or export budget data',
              'up.onImportBudget'
            ),
            importContactsButton: this.createImportButton(
              'fa-address-card',
              'Contacts',
              'Import or export contact data',
              'up.onImportContacts'
            ),
            importBoardUsersButton: this.createImportButton(
              'fa-users',
              'Board Users',
              'Import or export board user data',
              'up.onImportBoardUsers'
            )
          }
        }
      }
    };
  }

  loadRecord(record, context) {
    super.loadRecord(record, context);
    this.context = context;
    this.project = context.project;

    const buttonGrid = this.widgetMap.buttonGrid;
    const {
      importResourcesButton,
      importTasksButton,
      importClientsButton,
      importBudgetButton,
      importContactsButton,
      importBoardUsersButton
    } = buttonGrid.widgetMap;

    const canImport = this.context.user.can(GRANTS.grant_three);
    const isDemoUser = this.context.user.isDemoUser;

    importResourcesButton.disabled = isDemoUser || !canImport;
    importTasksButton.disabled = isDemoUser || !canImport;
    importClientsButton.disabled = isDemoUser || !canImport;
    importBudgetButton.disabled = isDemoUser || !canImport;
    importContactsButton.disabled = isDemoUser || !canImport;
    importBoardUsersButton.disabled = isDemoUser || !canImport;
  }

  onImportResources() {
    const { editorHandler, user } = this.context;
    editorHandler.openEditor(
      EDITOR_KEYS.importEditor,
      null,
      {
        importType: IMPORT_TYPES.resources,
        canEdit: user && user.can(GRANTS.grant_one),
        importHeader: IMPORT_HEADERS.resources,
        labelIndex: 12
      },
      { useExisting: true, by: "recordType" }
    );
  }

  onImportTasks() {
    const { editorHandler, user } = this.context;
    editorHandler.openEditor(
      EDITOR_KEYS.importEditor,
      null,
      {
        importType: IMPORT_TYPES.tasks,
        canEdit: user && user.can(GRANTS.grant_one),
        importHeader: IMPORT_HEADERS.tasks,
        labelIndex: 12
      },
      { useExisting: true, by: "recordType" }
    );
  }

  onImportClients() {
    const { editorHandler, user } = this.context;
    editorHandler.openEditor(
      EDITOR_KEYS.importEditor,
      null,
      {
        importType: IMPORT_TYPES.clients,
        canEdit: user && user.can(GRANTS.grant_one),
        importHeader: IMPORT_HEADERS.clients,
        labelIndex: 12
      },
      { useExisting: true, by: "recordType" }
    );
  }

  onImportBudget() {
    const { editorHandler, user } = this.context;
    editorHandler.openEditor(
      EDITOR_KEYS.importEditor,
      null,
      {
        importType: IMPORT_TYPES.taskBudget,
        canEdit: user && user.can(GRANTS.grant_one),
        importHeader: IMPORT_HEADERS.taskBudget,
        labelIndex: 12
      },
      { useExisting: true, by: "recordType" }
    );
  }

  onImportContacts() {
    const { editorHandler, user } = this.context;
    editorHandler.openEditor(
      EDITOR_KEYS.importEditor,
      null,
      {
        importType: IMPORT_TYPES.contacts,
        canEdit: user && user.can(GRANTS.grant_one),
        importHeader: IMPORT_HEADERS.contacts,
        labelIndex: 12
      },
      { useExisting: true, by: "recordType" }
    );
  }

  onImportBoardUsers() {
    const { editorHandler, user } = this.context;
    editorHandler.openEditor(
      EDITOR_KEYS.importEditor,
      null,
      {
        importType: IMPORT_TYPES.boardUsers,
        canEdit: user && user.can(GRANTS.grant_one),
        importHeader: IMPORT_HEADERS.boardUsers,
        labelIndex: 12
      },
      { useExisting: true, by: "recordType" }
    );
  }
}

SettingsImportExportTab.initClass();
